import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import ProfileSettings from '../components/settings/ProfileSettings';

const defaultSettings = {
  currency: 'KES',
  language: 'en',
  company_name: 'Roth Property Management',
  company_address: 'Nairobi, Kenya',
  company_phone: '+254700000000',
  company_email: 'info@rothproperty.co.ke',
  rent_due_day: 1,
  late_fee_percentage: 5,
  grace_period_days: 5,
  default_notification_channel: 'sms',
};

const Settings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [prefs, setPrefs] = useState({
    channel_email: 1,
    channel_sms: 1,
    channel_whatsapp: 1,
    channel_push: 1,
    rent_reminders_enabled: 1,
    payment_receipts_enabled: 1,
    maintenance_updates_enabled: 1,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [systemRes, prefsRes] = await Promise.all([
          axios.get('/api/settings'),
          axios.get('/api/settings/preferences'),
        ]);

        setSettings({ ...defaultSettings, ...(systemRes.data.settings || {}) });
        setPrefs(prev => ({ ...prev, ...(prefsRes.data.preferences || {}) }));
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings', settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings/preferences', prefs);
      toast.success('Notification preferences updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const canManageSystem = ['admin', 'manager', 'landlord'].includes(user?.user_level);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">
          {canManageSystem
            ? 'Your account, plus operating defaults and notification preferences.'
            : 'Your account details and how we contact you.'}
        </p>
      </div>

      {/* Account first: it is the only section every role can use. */}
      <ProfileSettings />

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-gray-500">Loading settings…</div>
      ) : (
        <div className="space-y-6">
          {canManageSystem && (
          <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">System configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default currency</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                >
                  <option value="KES">KES - Kenya Shilling</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - Pound</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">Example: {formatCurrency(1500, settings.currency)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                >
                  <option value="en">English</option>
                  <option value="sw">Swahili</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company name</label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company email</label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.company_email}
                  onChange={(e) => handleChange('company_email', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company phone</label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.company_phone}
                  onChange={(e) => handleChange('company_phone', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company address</label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.company_address}
                  onChange={(e) => handleChange('company_address', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rent due day</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.rent_due_day}
                  onChange={(e) => handleChange('rent_due_day', Number(e.target.value))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Late fee %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.late_fee_percentage}
                  onChange={(e) => handleChange('late_fee_percentage', Number(e.target.value))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grace period (days)</label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.grace_period_days}
                  onChange={(e) => handleChange('grace_period_days', Number(e.target.value))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default notification channel</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={settings.default_notification_channel}
                  onChange={(e) => handleChange('default_notification_channel', e.target.value)}
                >
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                type="button"
                disabled={saving}
                onClick={saveSettings}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </div>
          )}

          <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Notification preferences</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center gap-3"><input type="checkbox" checked={Boolean(prefs.channel_email)} onChange={(e) => setPrefs({ ...prefs, channel_email: e.target.checked ? 1 : 0 })} /> Email notices</label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={Boolean(prefs.channel_sms)} onChange={(e) => setPrefs({ ...prefs, channel_sms: e.target.checked ? 1 : 0 })} /> SMS reminders</label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={Boolean(prefs.channel_whatsapp)} onChange={(e) => setPrefs({ ...prefs, channel_whatsapp: e.target.checked ? 1 : 0 })} /> WhatsApp updates</label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={Boolean(prefs.channel_push)} onChange={(e) => setPrefs({ ...prefs, channel_push: e.target.checked ? 1 : 0 })} /> In-app notifications</label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={Boolean(prefs.rent_reminders_enabled)} onChange={(e) => setPrefs({ ...prefs, rent_reminders_enabled: e.target.checked ? 1 : 0 })} /> Rent reminders</label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={Boolean(prefs.payment_receipts_enabled)} onChange={(e) => setPrefs({ ...prefs, payment_receipts_enabled: e.target.checked ? 1 : 0 })} /> Payment receipts</label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={Boolean(prefs.maintenance_updates_enabled)} onChange={(e) => setPrefs({ ...prefs, maintenance_updates_enabled: e.target.checked ? 1 : 0 })} /> Maintenance updates</label>
            </div>

            <div className="flex justify-end mt-6">
              <button
                type="button"
                disabled={saving}
                onClick={savePreferences}
                className="px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Update preferences'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
